/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useCallback, useMemo } from 'react';
// redux
import {useDispatch, useSelector} from 'react-redux';
import { fetchAccountUsers, fetchAccountCommentingRoles, PRODUCT_COMMENTING } from '../../../../../state/thunks/account';
import { inviteUser, resendInvite, addUser, updateUser } from '../../../../../state/thunks/users';
import { toggleAccountUserForm, closeAccountUserForms } from '../../../../../state/slices/account';
// utils
import { formatRoleName } from './utils/userutils';
// material
import { ListTable, Group, Button, Title, Link, ToolTip, displayModal } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
// content
import DeleteUser from './deleteuser/deleteuser';
import UserForm from './userform/userform';
// styles
import injectSheet from 'react-jss';
import styles from './usersconfig_styles';

const defaultUserPermissions = ['settings.sites', 'settings.general', 'settings.adsTxt', 'manageEmbeds', 'home.adMetrics']
const defaultCols = { name: 'Name', email: 'Email', status: 'Status'}

/* ========== ~~~~~~~~~~ Users Config ~~~~~~~~~~ ========== */
const UsersConfig = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { accountUUID, userUUID: sessionUserUUID, permissions: sessionPermissions, mocked } = useSelector(state => state.session);
  const { accountUsers, sendingInvite, resendingInvite, userFormsOpened, updatingUsers, addingUserStatus, addingUserError, accountProducts } = useSelector(state => state.account);
  const hasCommenting = accountProducts?.includes(PRODUCT_COMMENTING)
  const [columns, setColumns] = useState(defaultCols)

  const [isOpened, setIsOpened] = useState(false);
  const [isUserFormShown, showNewUserForm] = useState(false);
  const [isScollIntoView, setIsScrollIntoView] = useState(false)

  useEffect(() => {
    let currentLocation = window.location.href;
    const hasUrlAnchor = currentLocation.includes("?view=publisherUser");
    if(hasUrlAnchor) setIsOpened(true)
  },[])

  useEffect(() => {
    if(isOpened && !isScollIntoView) scrollToConfig()
  }, [isOpened, isScollIntoView])

  const scrollToConfig= () => {
    let currentLocation = window.location.href;
    const hasUrlAnchor = currentLocation.includes("?view=publisherUser");
    if (hasUrlAnchor) {
      const anchorElem = document.getElementById('publisher-user');
      if(anchorElem){
          setTimeout(() => {
            anchorElem.scrollIntoView({ behavior: "smooth",  block: 'center'  })
            setIsScrollIntoView(true)
          }, 700)
      }
    }
  }

  useEffect(() => {
    return () => {
      // close all forms when user go to other page
      dispatch(closeAccountUserForms())
      showNewUserForm(false)
    }
  },[])

  useEffect(() => {
    dispatch(fetchAccountUsers(accountUUID))
    dispatch(fetchAccountCommentingRoles(accountUUID))
  }, [accountUUID])

  useEffect(() => {
    let cols = {...defaultCols}
    if(hasCommenting) cols.badgeType = 'Badge Type'
    cols.edit = ''
    setColumns(cols)
  }, [hasCommenting])

  const hasChangeUserPermissions = useMemo(() => sessionPermissions?.includes('settings.users') || false, [sessionPermissions]);

  const handleToggle = () => {
    setIsOpened(!isOpened)
    dispatch(closeAccountUserForms())
    showNewUserForm(false)
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleToggle}
      isOpened={isOpened}
      configTitle={'View'}
      cancelTitle={'Close'}
      title='Users Management'
    />
  );

  const formattedUserData = () => accountUsers.map((user, idx) => {
    const disabled = sessionUserUUID === user.userUUID;
    const userFullName = `${user.firstName} ${user.lastName}`;
    const editFormTitle = `Edit - ${userFullName}`;
    const commentingUser = user.commentingUser
    return {
      name: (
        userFullName.length > 20 ? 
        <ToolTip position={'top'} trigger={'mouseenter focus'} content={userFullName} zIndex={9999}>
          <div className={'namecolumn'}>{userFullName}</div>
        </ToolTip>
        :
        <div>{userFullName}</div>
      ),
      email: user.email,
      status :user.userStatus.replaceAll('_', ' '),
      badgeType: commentingUser?.roleName || '-',
      edit: (
        <div className={'actioncolumn'}>
          { user.userStatus === 'CREATED' && 
            <Button 
              name={'inviteuser'}
              type={'action'} 
              variant={'primary'}
              text={'Invite User'} 
              operation={'send'}
              processing={sendingInvite}
              removeLoaderIcon={true} 
              action={() => handleSendInvite(user.userUUID)}
            />
          }
          { mocked && user.userStatus === 'INVITE_SENT' &&
            <Button 
              name={'inviteuser'}
              type={'action'} 
              variant={'primary'}
              text={'Resend Invite'} 
              operation={'send'}
              processing={sendingInvite}
              removeLoaderIcon={true} 
              action={() => handleSendInvite(user.userUUID)}
            />
          }
          <Link icon={'edit'} disabled={disabled} handleClick={() => toggleUserForm(user.userUUID)}/>
          <Link icon={'trash'} disabled={disabled} handleClick={() => showDeleteConfirm(user)}/>
        </div>
      ),
      expand: userFormsOpened.includes(user.userUUID),
      expandable: userFormsOpened.includes(user.userUUID) && (
        <UserForm 
          title={editFormTitle} 
          data={{...user, accountUUID}} 
          isProcessing={updatingUsers.includes(user.userUUID)} 
          onSave={handleUpdateUser} 
          onCancel={() => toggleUserForm(user.userUUID)}
          onResendInvite={() => handleResendInvite(user.userUUID)}
          isResendingInvite={resendingInvite.includes(user.userUUID)}
        />
      )
    }
  })

  const toggleUserForm = userUUID => dispatch(toggleAccountUserForm(userUUID))

  const showDeleteConfirm = user => dispatch(displayModal(true, <DeleteUser user={user} userHasCommenting={user.commentingUser} />)) 

  const handleSendInvite = userUUID =>  dispatch(inviteUser(userUUID))

  const handleResendInvite = userUUID => dispatch(resendInvite(userUUID))

  const handleAddUser = useCallback(async (newData) => {
    const response = await dispatch(addUser(newData))
    if(!response.error) showNewUserForm(false)
  }, []);

  const handleUpdateUser = useCallback(async (newData) => {
    const response = await dispatch(updateUser(newData))
    if(!response.error) toggleUserForm(newData.userUUID)
  }, []);

  return (
    <ExpandableSection
      className={`usersconfig ${props.classes.usersconfig}`}
      controller={controller}
      isOpened={isOpened}
      handleCancel={handleToggle}
      cancelTitle={'Close'}
    >
      {isOpened && <div id="publisher-user">
        <SectionCard>
          <Group name={'users-header'}>
            <Title><h2>{ hasChangeUserPermissions ? `Users` : `Permission Denied`}</h2></Title>
            { !isUserFormShown && hasChangeUserPermissions && <Button text={'New User'} variant={'primary'} handleClick={() => showNewUserForm(true)} /> }
          </Group>
          { isUserFormShown && 
            <UserForm 
              data={{accountUUID, permissions: defaultUserPermissions}}
              title={'New User'} 
              isProcessing={addingUserStatus === 'pending'} 
              onSave={handleAddUser} 
              onCancel={() => showNewUserForm(false)} 
             addingUserError={addingUserError}
            />
          } 
          { hasChangeUserPermissions ? 
            <ListTable
              name={`users`}
              size={`full`}
              columnHeads={columns}
              data={formattedUserData()}
              rowsPerPage={5}
              paginationLocation={`bottom`}
            />
            :
            <Group name={'permission-denied'}>
              <Title>
                <h4>You don't have permission or access to manage users</h4>
              </Title>
            </Group>
          }
        </SectionCard>
      </div>}
    </ExpandableSection>
  )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default UsersConfig 